import { useEffect, useRef } from "react";

type EventListenerOptions = boolean | AddEventListenerOptions;

/**
 * Custom hook to manage event listeners
 * @param eventType - The event type to listen for (e.g., 'click', 'resize')
 * @param listener - The event listener callback
 * @param element - The target element to attach the event listener to (default: window)
 * @param options - Options for the event listener
 */
function useEventListener<K extends keyof HTMLElementEventMap>(
    eventType: K,
    listener: (event: HTMLElementEventMap[K]) => void,
    element: HTMLElement | Document | Window | null = window,
    options?: EventListenerOptions
) {
    const savedListener = useRef<(event: HTMLElementEventMap[K]) => void>();

    // Update the ref to the latest listener
    useEffect(() => {
        savedListener.current = listener;
    }, [listener]);

    useEffect(() => {
        if (!element || !(element instanceof EventTarget)) {
            console.warn("The provided element is not a valid EventTarget.");
            return;
        }

        // Create a generic event listener that delegates to the saved listener
        const eventListener = (event: Event) => {
            savedListener.current?.(event as HTMLElementEventMap[K]);
        };

        element.addEventListener(eventType, eventListener, options);

        // Cleanup
        return () => {
            element.removeEventListener(eventType, eventListener, options);
        };
    }, [eventType, element, options]);
}

export default useEventListener;
